import type { FC } from 'react';
import React, { useMemo } from 'react';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';
import { makeConfig, PaginatedTable2 } from '@pflegenavi/web-components';
import {
  useFormatDate,
  useFormatting,
} from '@pflegenavi/frontend/localization';
import type { TransformedCashTransactionGroup } from '@pflegenavi/frontend/api-nursing-home';
import { useCashListConfiguration } from '@pflegenavi/frontend/api-nursing-home';
import { useFeatureFlagEnabled } from '@pflegenavi/frontend/feature-flags';
import { nhAppMainPages } from '@pflegenavi/frontend/routing';
import { FeatureFlag } from '@pflegenavi/shared/api';
import { TIME_FORMAT } from '@pflegenavi/shared/constants';
import { LinkedTableSection } from './LinkedTableSection';
import { useLinkedTablePagination } from './useLinkedTablePagination';

type LinkedTransfer =
  TransformedCashTransactionGroup['linkedCashListTransactions'][number];

type ColumnNames = 'cashList' | 'employee' | 'date' | 'amount' | 'notes';

/** Cash transactions of other groups linked to this one; a row opens that group. */
export const LinkedTransfersTable: FC<{ transfers: LinkedTransfer[] }> = ({
  transfers,
}) => {
  const { t } = useTranslation();
  const { fCurrencyCents } = useFormatting();
  const fDate = useFormatDate();
  const navigate = useNavigate();
  const { cashLists } = useCashListConfiguration();
  const groupDetailPath = useFeatureFlagEnabled(
    FeatureFlag.AccountingPaymentsNew
  )
    ? nhAppMainPages.NURSING_HOME_ACCOUNTING_GROUP_DETAIL
    : nhAppMainPages.CASH_MANAGEMENT_GROUP_DETAIL;

  const { page, pageSize, pagination } = useLinkedTablePagination(
    transfers.length
  );
  const rows = useMemo(
    () => transfers.slice(page * pageSize, (page + 1) * pageSize),
    [transfers, page, pageSize]
  );

  const config = useMemo(
    () =>
      makeConfig<LinkedTransfer, ColumnNames, never>({
        showAll: undefined,
        hideEmptyRows: true,
        onClickRow: (row) =>
          navigate(generatePath(groupDetailPath, { groupId: row.groupId })),
        columns: [
          {
            key: 'cashList',
            label: t(
              'cashManagement.group-detail.cash-transactions-table.cash-list'
            ),
            align: 'left',
            component: ({ row }) =>
              cashLists.find((cashList) => cashList.id === row.cashListId)
                ?.name ?? '--',
          },
          {
            key: 'employee',
            label: t(
              'cashManagement.group-detail.cash-transactions-table.employee'
            ),
            align: 'left',
            component: ({ row }) =>
              row.employee
                ? `${row.employee.firstName ?? ''} ${
                    row.employee.lastName ?? ''
                  }`.trim()
                : '--',
          },
          {
            key: 'date',
            label: t(
              'cashManagement.group-detail.cash-transactions-table.date'
            ),
            align: 'left',
            component: ({ row }) => fDate(row.date, TIME_FORMAT),
          },
          {
            key: 'amount',
            label: t(
              'cashManagement.group-detail.cash-transactions-table.amount'
            ),
            align: 'right',
            component: ({ row }) => (
              <Typography variant="subtitle2" noWrap>
                {fCurrencyCents(row.amount)}
              </Typography>
            ),
          },
          {
            key: 'notes',
            label: t(
              'cashManagement.group-detail.cash-transactions-table.notes'
            ),
            align: 'left',
            component: ({ row }) => row.notes || '--',
          },
        ],
        pagination,
      }),
    [t, fDate, fCurrencyCents, navigate, groupDetailPath, cashLists, pagination]
  );

  return (
    <LinkedTableSection
      title={t('cashManagement.group-detail.overview.linked-transfers-title')}
      count={transfers.length}
    >
      <PaginatedTable2
        name="CashTransactionGroupDetailLinkedTransferTable"
        config={config}
        rows={rows}
        isLoading={false}
      />
    </LinkedTableSection>
  );
};
